import { useEffect, useState } from "react";
import { Outlet, Route, Routes, useNavigate, Link } from "react-router-dom";
import { Check, Upload, Copy, MessageSquare, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, money, openFile } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Btn, Chip, Empty, Skeleton, Eyebrow } from "@/components/brand";
import { AppHeader, Thread, useLoad } from "@/components/app";
import { BureauCards, ReportView, UpdatesView } from "@/components/report";
import { Pills, FormError } from "@/components/forms";
import { DEFAULT_SQUARE, PAY_COPY, useSeo } from "@/components/site";

const TABS = [["Overview", "/portal"], ["Credit Report", "/portal/report"], ["Updates", "/portal/updates"], ["Documents", "/portal/documents"], ["Messages", "/portal/messages"], ["Refer $100", "/portal/refer"], ["Payments", "/portal/payments"], ["Settings", "/portal/settings"]];

export function CallPanel({ calendly, onDone }) {
  const [when, setWhen] = useState("ASAP");
  const [at, setAt] = useState("");
  const [topic, setTopic] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const send = async () => {
    setErr("");
    try {
      await api.post("/client/call-requests", { when: when === "Pick date & time" ? "Specific time" : when, whenAt: when === "Pick date & time" ? at : null, topic });
      setSent(true);
      onDone && onDone();
    } catch (e) { setErr(errMsg(e)); }
  };
  return (
    <div className="bg-white border border-tanline p-6 sm:p-7 fade-up" data-testid="call-panel">
      <p className="eyebrow text-red7">Request a call</p>
      {calendly && <Btn as="a" href={calendly} target="_blank" rel="noopener noreferrer" variant="gold" arrow className="mt-4" data-testid="calendly-btn">Pick a time on my calendar</Btn>}
      <p className="text-sm text-muted7 mt-5">{calendly ? "Or request a callback:" : "Request a callback:"} When is best?</p>
      <div className="mt-3"><Pills options={["ASAP", "Today afternoon", "Today evening", "Tomorrow morning", "Pick date & time"]} value={when} onChange={setWhen} multi={false} testid="call-when" /></div>
      {when === "Pick date & time" && <input type="datetime-local" className="field mt-3 max-w-xs" value={at} onChange={(e) => setAt(e.target.value)} data-testid="call-datetime" />}
      <input className="field mt-3" placeholder="Topic (optional)" maxLength={300} value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="call-topic" />
      <div className="flex items-center gap-4 mt-4">
        <Btn onClick={send} disabled={sent} data-testid="send-call-request-btn">Send call request</Btn>
        {sent && <Chip tone="ok" data-testid="call-request-confirmed"><Check className="w-3 h-3" />Request sent — Seven will call you</Chip>}
      </div>
      <FormError msg={err} />
    </div>
  );
}

function Pending({ me }) {
  return (
    <div className="max-w-3xl mx-auto px-4 py-16">
      <div className="bg-ink text-white border-l-4 border-gold p-8 sm:p-10" data-testid="access-pending">
        <p className="eyebrow text-gold">Access pending.</p>
        <h1 className="f-display text-3xl sm:text-4xl mt-3">Welcome, {me?.client?.name?.split(" ")[0]}.</h1>
        <p className="text-white/75 mt-4">Your record is in onboarding. Seven will activate full access after review.</p>
      </div>
    </div>
  );
}

function Overview({ me }) {
  const { data } = useLoad("/client/overview");
  const [call, setCall] = useState(false);
  const nav = useNavigate();
  if (!data) return <Skeleton className="h-96" />;
  const c = data.client;
  const ob = c.onboarding || {};
  const steps = [
    ["Enroll in MyFreeScoreNow", ob.mfsnEnrolled, data.mfsnLink ? <a href={data.mfsnLink} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm">Enroll ↗</a> : <span className="text-xs text-muted7">Seven confirms this</span>],
    ["Upload photo ID", ob.idReceived, <Link to="/portal/documents?type=photo_id" className="btn btn-outline btn-sm">Upload</Link>],
    ["Proof of address", ob.proofOfAddress, <Link to="/portal/documents?type=proof_of_address" className="btn btn-outline btn-sm">Upload</Link>],
    ["Upload your MFSN report", ob.reportUploaded, <Link to="/portal/report" className="btn btn-outline btn-sm">Upload</Link>],
  ];
  return (
    <div className="space-y-5">
      <div className="hero-panel brackets p-7 sm:p-10" data-testid="overview-hero">
        <h1 className="f-display text-4xl sm:text-5xl">Welcome, {c.name.split(" ")[0]}.</h1>
        <p className="text-muted7 mt-3 text-lg">Your account is active. I'm here whenever you need me.</p>
        <div className="flex flex-wrap gap-3 mt-7">
          <Btn variant="gold" arrow onClick={() => nav("/portal/messages")} data-testid="message-seven-btn">Message Seven</Btn>
          <Btn variant="outline" onClick={() => setCall(!call)} data-testid="request-call-btn">Request a Call</Btn>
        </div>
      </div>
      {call && <CallPanel calendly={data.calendlyLink} />}
      {data.scores && <BureauCards scores={data.scores.current} baseline={data.scores.baseline} />}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white border border-tanline p-6" data-testid="getting-started">
          <p className="eyebrow text-muted7">Getting started</p>
          <ol className="mt-4 divide-y divide-tanline">
            {steps.map(([t, done, action], i) => (
              <li key={t} className="py-3.5 flex items-center justify-between gap-3">
                <span className="flex items-center gap-3"><span className="f-mono text-gold text-xs font-semibold">0{i + 1}</span><span className="font-medium text-[15px]">{t}</span></span>
                {done ? <Chip tone="ok"><Check className="w-3 h-3" />Done</Chip> : action}
              </li>
            ))}
          </ol>
        </div>
        <div className="bg-white border border-tanline p-6 flex flex-col" data-testid="latest-update">
          <p className="eyebrow text-muted7">Latest from Seven</p>
          {data.latestUpdate ? (
            <div className="mt-4 flex-1">
              <h3 className="f-display text-2xl">{data.latestUpdate.name}</h3>
              <p className="text-sm text-muted7 mt-2">{data.latestUpdate.summary}</p>
              <Link to="/portal/updates" className="nav-link text-sm font-semibold mt-4 inline-block">See verified results →</Link>
            </div>
          ) : <p className="text-muted7 mt-4 flex-1">Seven will post updates here once they're verified.</p>}
        </div>
      </div>
      <div className="bg-white border border-tanline p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="next-payment">
        <div>
          <p className="eyebrow text-muted7">Next payment</p>
          <p className="f-display text-2xl mt-2">{data.nextPayment ? `${money(data.nextPayment.amount)} due ${fmtDate(data.nextPayment.due)}` : "You're all paid up."}</p>
        </div>
        <Btn as="a" href={data.squareUrl || DEFAULT_SQUARE} target="_blank" rel="noopener noreferrer" variant="gold" arrow data-testid="overview-pay-btn">Pay with Square</Btn>
      </div>
    </div>
  );
}

function Report() {
  const { data, reload } = useLoad("/client/report");
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  useEffect(() => {
    if (!job || job.status !== "parsing") return;
    const t = setInterval(async () => {
      const { data: s } = await api.get(`/client/report/import/${job.id}`);
      setJob(s);
      if (s.status !== "parsing") { clearInterval(t); reload(); s.status === "saved" ? toast.success("Report updated automatically.") : toast.error(s.error || "Couldn't read that report."); }
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [job?.id, job?.status]);
  const upload = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try { const { data: j } = await api.post("/client/report", fd); setJob(j); } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); e.target.value = ""; }
  };
  const last = job || data?.lastUpload;
  return (
    <div className="space-y-5">
      <div className="bg-white border-2 border-gold p-6 flex flex-col md:flex-row md:items-center justify-between gap-5" data-testid="report-upload-card">
        <p className="text-[15px] max-w-xl">Pulled a new MyFreeScoreNow report? Upload it here — your scores, accounts and inquiries update automatically.</p>
        <div className="flex flex-col items-start md:items-end gap-2">
          <label className={`btn btn-black ${busy || last?.status === "parsing" ? "opacity-60 pointer-events-none" : ""}`} data-testid="report-upload-btn">
            <Upload className="w-4 h-4" />{last?.status === "parsing" ? "Reading report…" : "Upload report"}
            <input type="file" accept="application/pdf,.html,.htm,text/html" className="hidden" onChange={upload} data-testid="report-upload-input" />
          </label>
          {last?.status === "saved" && <Chip tone="ok" data-testid="report-uploaded-chip">✓ Uploaded {fmtDate(last.createdAt)} · updated automatically</Chip>}
          {last?.status === "error" && <Chip tone="neg">Couldn't read the last upload — try the PDF</Chip>}
        </div>
      </div>
      {data ? <ReportView r={data} /> : <Skeleton className="h-96" />}
    </div>
  );
}

function Updates() {
  const { data } = useLoad("/client/updates");
  return data ? <UpdatesView items={data} /> : <Skeleton className="h-64" />;
}

const DOC_TYPES = [["mfsn_report", "MyFreeScoreNow report"], ["proof_of_address", "Proof of address"], ["photo_id", "Photo ID"], ["bureau_letter", "Bureau letter"], ["other", "Other"]];

function Documents() {
  const { data, reload } = useLoad("/client/documents");
  const [type, setType] = useState(new URLSearchParams(window.location.search).get("type") || "mfsn_report");
  const [files, setFiles] = useState([]);
  const [descs, setDescs] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const pick = (e) => {
    const fs = [...e.target.files];
    setErr("");
    if (fs.some((f) => f.size > 8 * 1024 * 1024)) return setErr("Each file must be 8 MB or smaller.");
    const pdf = fs.filter((f) => f.type === "application/pdf");
    if (fs.length > 4 || (pdf.length && fs.length > 1)) return setErr("Upload up to 4 images or 1 PDF at a time.");
    setFiles(fs);
    setDescs(fs.map(() => ""));
  };
  const submit = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("type", type);
    fd.append("descriptions", descs.join("||"));
    files.forEach((f) => fd.append("files", f));
    try { await api.post("/client/documents", fd); toast.success("Received — thank you."); setFiles([]); reload(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const label = Object.fromEntries(DOC_TYPES);
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="bg-white border border-tanline p-6 space-y-5" data-testid="documents-upload">
        <div><h2 className="f-display text-3xl">Upload documents</h2><p className="text-sm text-muted7 mt-2">JPEG, PNG or PDF up to 8 MB — up to 4 images or 1 PDF. Please upload here; never send documents by email.</p></div>
        <div><label className="label" htmlFor="doc-type">Document type</label>
          <select id="doc-type" className="field" value={type} onChange={(e) => setType(e.target.value)} data-testid="doc-type-select">{DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        <label className="block border border-dashed border-tanline bg-cream p-8 text-center cursor-pointer hover:border-gold transition-colors" data-testid="doc-dropzone">
          <Upload className="w-6 h-6 mx-auto text-goldtext" /><p className="text-sm mt-2">Choose files</p>
          <input type="file" multiple accept="image/jpeg,image/png,application/pdf" className="hidden" onChange={pick} data-testid="doc-file-input" />
        </label>
        {files.map((f, i) => (
          <div key={i} className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <span className="text-sm font-medium truncate sm:w-40">{f.name}</span>
            <input className="field" placeholder="Short description" value={descs[i]} onChange={(e) => setDescs(descs.map((d, k) => (k === i ? e.target.value : d)))} data-testid={`doc-desc-${i}`} />
          </div>
        ))}
        <FormError msg={err} />
        <Btn onClick={submit} disabled={!files.length || busy} data-testid="doc-submit-btn">{busy ? "Uploading…" : "Upload securely"}</Btn>
      </div>
      <div className="bg-white border border-tanline p-6" data-testid="documents-on-file">
        <p className="eyebrow text-muted7">On file</p>
        {!data ? <Skeleton className="h-40 mt-4" /> : !data.length ? <p className="text-muted7 mt-4">Nothing on file yet.</p> : (
          <ul className="divide-y divide-tanline mt-3">
            {data.map((d) => (
              <li key={d.id} className="py-3 flex justify-between gap-3 items-center">
                <button onClick={() => openFile(`/client/files/${d.id}`)} className="text-left"><p className="font-medium text-[15px]">{label[d.type] || d.type}</p><p className="text-xs text-muted7">{d.description || d.filename}</p></button>
                <div className="text-right"><p className="f-mono text-[10px] text-muted7">Received {fmtDate(d.createdAt)}</p>{d.deleteAt && <p className="f-mono text-[10px] text-goldtext">Auto-deletes {fmtDate(d.deleteAt)}</p>}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Messages({ me }) {
  const { data, reload } = useLoad("/client/messages");
  const [call, setCall] = useState(false);
  useEffect(() => { const t = setInterval(reload, 15000); return () => clearInterval(t); /* eslint-disable-next-line */ }, []);
  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3"><h1 className="f-display text-4xl">Talk to Seven anytime.</h1></div>
      {call && <CallPanel calendly={me?.calendlyLink} />}
      <Thread messages={data || []} me="client" filePrefix="/client/files" onCall={() => setCall(!call)} onSend={async (fd) => { await api.post("/client/messages", fd); reload(); }} />
    </div>
  );
}

const REF_STATUS = { New: "Seven reached out", Contacted: "Seven reached out", "Consultation booked": "Consultation booked", Enrolled: "Enrolled" };

function Refer() {
  const { data, reload } = useLoad("/client/referrals");
  const [f, setF] = useState({ referredName: "", referredPhone: "", goals: [] });
  const [err, setErr] = useState("");
  if (!data) return <Skeleton className="h-80" />;
  const link = `https://se7encapital.com/r/${data.code}`;
  const send = async (e) => {
    e.preventDefault();
    try { await api.post("/client/referrals", f); toast.success("Sent — I'll reach out personally."); setF({ referredName: "", referredPhone: "", goals: [] }); reload(); } catch (ex) { setErr(errMsg(ex)); }
  };
  return (
    <div className="space-y-5">
      <div className="bg-ink text-white p-7 sm:p-10 brackets on-dark flex flex-col md:flex-row md:items-end justify-between gap-6">
        <h1 className="f-display text-3xl sm:text-4xl max-w-lg">${data.payout} for every friend who enrolls.</h1>
        <div><p className="eyebrow text-white/60">Earned</p><p className="f-display text-5xl font-semibold gold-foil" data-testid="referral-earned">{money(data.earned)}</p></div>
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <form onSubmit={send} className="bg-white border border-tanline p-6 space-y-4" data-testid="portal-refer-form">
          <input className="field" placeholder="Their name" value={f.referredName} onChange={(e) => setF({ ...f, referredName: e.target.value })} required data-testid="portal-refer-name" />
          <input className="field" type="tel" placeholder="Their phone" value={f.referredPhone} onChange={(e) => setF({ ...f, referredPhone: e.target.value })} required data-testid="portal-refer-phone" />
          <Pills options={["Repair", "LLC", "Funding"]} value={f.goals} onChange={(g) => setF({ ...f, goals: g })} testid="portal-refer-goal" />
          <FormError msg={err} />
          <Btn type="submit" variant="red" arrow data-testid="portal-refer-send">Send</Btn>
        </form>
        <div className="bg-white border border-tanline p-6">
          <p className="eyebrow text-muted7">Your personal link</p>
          <p className="f-mono text-sm mt-3 break-all" data-testid="portal-refer-link">{link}</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <button className="btn btn-black btn-sm" onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }} data-testid="portal-refer-copy"><Copy className="w-4 h-4" />Copy</button>
            <a className="btn btn-outline btn-sm" href={`sms:?&body=${encodeURIComponent(`Book a free consult with Seven at Se7en Capital Group: ${link}`)}`}><MessageSquare className="w-4 h-4" />Text</a>
            {navigator.share && <button className="btn btn-outline btn-sm" onClick={() => navigator.share({ url: link, title: "Se7en Capital Group" })}><Share2 className="w-4 h-4" />Share</button>}
          </div>
        </div>
      </div>
      <div className="bg-white border border-tanline p-6" data-testid="portal-referrals-list">
        <p className="eyebrow text-muted7">Your referrals</p>
        {!data.referrals.length ? <p className="text-muted7 mt-3">No referrals yet.</p> : (
          <ul className="divide-y divide-tanline mt-3">{data.referrals.map((r) => (
            <li key={r.id} className="py-3 flex justify-between gap-3"><span className="font-medium">{r.referredName}</span>
              {r.payoutStatus === "paid" ? <Chip tone="ok">${data.payout} paid</Chip> : <Chip tone={r.status === "Enrolled" ? "ok" : "pend"}>{REF_STATUS[r.status] || r.status}</Chip>}</li>
          ))}</ul>
        )}
      </div>
    </div>
  );
}

function Payments() {
  const { data } = useLoad("/client/payments");
  if (!data) return <Skeleton className="h-80" />;
  const square = data.squareUrl || DEFAULT_SQUARE;
  return (
    <div className="space-y-5">
      {data.payments.length === 0 && <Empty title="No payment plan yet." text="Seven will set up your program here." />}
      {data.payments.filter((p) => p.program !== "sweep" || data.payments.length === 1).slice(0, 1).map((p) => {
        const paid = p.plan.filter((i) => i.paidAt).length;
        const next = p.plan.find((i) => !i.paidAt);
        return (
          <div key={p.id} className="hero-panel brackets p-7 sm:p-9" data-testid="program-card">
            <p className="eyebrow text-muted7">Your program</p>
            <h1 className="f-display text-3xl sm:text-4xl mt-2">{p.label || p.program}</h1>
            <div className="flex gap-1.5 mt-6">{p.plan.map((i, k) => <span key={k} className={`h-2 flex-1 ${i.paidAt ? "bg-gold" : "bg-tan"}`} />)}</div>
            <p className="text-sm text-muted7 mt-2">{paid} of {p.plan.length} paid{next ? ` · next ${money(next.amount)} due ${fmtDate(next.due)}` : " · paid in full"}</p>
            <Btn as="a" href={square} target="_blank" rel="noopener noreferrer" variant="gold" arrow className="mt-6" data-testid="payments-pay-btn">Pay with Square</Btn>
          </div>
        );
      })}
      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-white border border-tanline border-l-4 !border-l-red7 p-6">
          <p className="eyebrow text-red7">Add-on</p>
          <h3 className="f-display text-2xl mt-2">Inquiry Sweep · <span className="gold-foil font-semibold">{money(data.sweepPrice)}</span></h3>
          <Btn as="a" href={square} target="_blank" rel="noopener noreferrer" variant="red" arrow className="mt-4 btn-sm" data-testid="addon-sweep-btn">Get my sweep</Btn>
        </div>
        <div className="bg-white border border-tanline p-6">
          <p className="eyebrow text-muted7">Square accepts</p>
          <div className="flex flex-wrap gap-2 mt-3">{["Any debit or credit card", "Cash App Pay", "Apple Pay", "Afterpay"].map((t) => <Chip key={t}>{t}</Chip>)}</div>
          <p className="text-sm text-muted7 mt-4">{PAY_COPY}</p>
          <Link to="/portal/messages" className="nav-link text-sm font-semibold mt-3 inline-block">Need a different plan? Message Seven →</Link>
        </div>
      </div>
      <div className="bg-white border border-tanline p-6" data-testid="payment-history">
        <p className="eyebrow text-muted7">Payment history</p>
        <ul className="divide-y divide-tanline mt-3">
          {data.payments.flatMap((p) => p.plan.filter((i) => i.paidAt).map((i, k) => (
            <li key={p.id + k} className="py-3 flex justify-between text-sm"><span>{p.label || p.program} · {p.method}</span><span className="font-semibold">{money(i.amount)} · {fmtDate(i.paidAt)}</span></li>
          )))}
        </ul>
      </div>
    </div>
  );
}

function Settings() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ name: user.name, phone: user.phone || "" });
  const [prefs, setPrefs] = useState(user.notificationPrefs || {});
  const [pw, setPw] = useState({ current: "", new: "" });
  const save = async () => {
    try { const { data } = await api.patch("/auth/me", { ...f, notificationPrefs: prefs }); setUser(data); toast.success("Saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const changePw = async () => {
    try { await api.post("/auth/change-password", pw); setPw({ current: "", new: "" }); toast.success("Password updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggles = [["email", "Email notifications"], ["sms", "Text notifications"], ["results", "New verified results"], ["payment", "Payment reminders"], ["message", "Messages from Seven"], ["report", "Report posted"]];
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="bg-white border border-tanline p-6 space-y-4" data-testid="portal-settings">
        <h2 className="f-display text-3xl">Your details</h2>
        <div><label className="label">Name</label><input className="field" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="settings-name" /></div>
        <div><label className="label">Phone</label><input className="field" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="settings-phone" /></div>
        <div><label className="label">Email</label><input className="field bg-tancard" value={user.email} readOnly /></div>
        <p className="eyebrow text-muted7 pt-3">Notifications</p>
        {toggles.map(([k, l]) => (
          <label key={k} className="flex items-center justify-between py-1.5 text-[15px]">{l}<input type="checkbox" className="w-5 h-5 accent-[#111]" checked={prefs[k] !== false} onChange={(e) => setPrefs({ ...prefs, [k]: e.target.checked })} data-testid={`pref-${k}`} /></label>
        ))}
        <p className="text-xs text-muted7">Text messages turn on once Seven connects texting.</p>
        <Btn onClick={save} data-testid="settings-save-btn">Save</Btn>
      </div>
      <div className="bg-white border border-tanline p-6 space-y-4 h-fit">
        <h2 className="f-display text-3xl">Password</h2>
        <input className="field" type="password" placeholder="Current password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="settings-current-pw" />
        <input className="field" type="password" placeholder="New password (8+ characters)" value={pw.new} onChange={(e) => setPw({ ...pw, new: e.target.value })} data-testid="settings-new-pw" />
        <Btn variant="outline" onClick={changePw} data-testid="settings-pw-btn">Update password</Btn>
        {!user.emailVerified && <div className="pt-4 border-t border-tanline"><p className="text-sm text-muted7">Your email isn't confirmed yet.</p><button className="nav-link text-sm font-semibold mt-1" onClick={() => api.post("/auth/resend-verification").then(() => toast.success("Confirmation email sent"))} data-testid="resend-verify-btn">Resend confirmation email</button></div>}
      </div>
    </div>
  );
}

export default function Portal() {
  useSeo("Client Portal");
  const { logout } = useAuth();
  const nav = useNavigate();
  const { data: me } = useLoad("/client/me");
  const out = () => { logout(); nav("/login"); };
  return (
    <div className="min-h-screen bg-white app-portal">
      <AppHeader tabs={me?.client?.portalActive ? TABS : []} base="/portal" onLogout={out} testid="portal-header" />
      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-10">
        {!me ? <Skeleton className="h-96" /> : !me.client.portalActive ? <Pending me={me} /> : (
          <Routes>
            <Route index element={<Overview me={me} />} />
            <Route path="report" element={<Report />} />
            <Route path="updates" element={<Updates />} />
            <Route path="documents" element={<Documents />} />
            <Route path="messages" element={<Messages me={me} />} />
            <Route path="refer" element={<Refer />} />
            <Route path="payments" element={<Payments />} />
            <Route path="settings" element={<Settings />} />
          </Routes>
        )}
      </main>
      <Outlet />
    </div>
  );
}

export { Eyebrow };
